import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Input from '../../components/Common/Input';
import Textarea from '../../components/Common/Textarea';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/swal';
import { ArrowLeft, Plus, X } from 'lucide-react';

const ProjectForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    owner: '',
    total_value: '',
    min_sqm: '',
    max_sqm: '',
    min_price: '',
    max_price: '',
    min_room_count: '',
    extra_info: '',
  });
  const [blocks, setBlocks] = useState([{ name: '' }]);

  useEffect(() => {
    if (isEditMode) {
      fetchProject();
    }
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/projects/${id}`);
      const project = response.data;
      setFormData({
        name: project.name || '',
        location: project.location || '',
        owner: project.owner || '',
        total_value: project.total_value || '',
        min_sqm: project.min_sqm || '',
        max_sqm: project.max_sqm || '',
        min_price: project.min_price || '',
        max_price: project.max_price || '',
        min_room_count: project.min_room_count || '',
        extra_info: project.extra_info || '',
      });
      if (project.blocks && project.blocks.length > 0) {
        setBlocks(project.blocks.map(b => ({ name: b.name })));
      }
    } catch (error) {
      console.error('Proje yüklenemedi:', error);
      showError('Proje yüklenemedi');
      navigate('/projects');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleBlockChange = (index, value) => {
    const newBlocks = [...blocks];
    newBlocks[index].name = value;
    setBlocks(newBlocks);
  };

  const addBlock = () => {
    setBlocks([...blocks, { name: '' }]);
  };

  const removeBlock = (index) => {
    const newBlocks = blocks.filter((_, i) => i !== index);
    setBlocks(newBlocks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        blocks: blocks.filter(b => b.name.trim() !== ''),
      };
      
      if (isEditMode) {
        await api.put(`/projects/${id}`, dataToSend);
        showSuccess('Proje başarıyla güncellendi');
      } else {
        await api.post('/projects', dataToSend);
        showSuccess('Proje başarıyla eklendi');
      }
      navigate('/projects');
    } catch (error) {
      console.error('Proje eklenirken hata:', error);
      showError('Proje kaydedilirken bir hata oluştu');
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Button variant="secondary" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Proje Düzenle' : 'Yeni Proje Ekle'}
          </h1>
        </div>
        <Card title="Temel Bilgiler" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Proje Adı"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Örn: Lüks Rezidans İstanbul"
              required
            />
            <Input
              label="Konum"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Örn: İstanbul, Beşiktaş"
              required
            />
            <Input
              label="Proje Sahibi"
              name="owner"
              value={formData.owner}
              onChange={handleChange}
              placeholder="Örn: ABC İnşaat A.Ş."
              required
            />
            <Input
              label="Toplam Değer (₺)"
              name="total_value"
              type="number"
              value={formData.total_value}
              onChange={handleChange}
              placeholder="Örn: 150000000"
            />
          </div>
        </Card>

        <Card title="Metrekare ve Fiyat Aralığı" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Min Metrekare (m²)"
              name="min_sqm"
              type="number"
              value={formData.min_sqm}
              onChange={handleChange}
              placeholder="Örn: 85"
            />
            <Input
              label="Max Metrekare (m²)"
              name="max_sqm"
              type="number"
              value={formData.max_sqm}
              onChange={handleChange}
              placeholder="Örn: 250"
            />
            <Input
              label="Min Fiyat (₺)"
              name="min_price"
              type="number"
              value={formData.min_price}
              onChange={handleChange}
              placeholder="Örn: 2500000"
            />
            <Input
              label="Max Fiyat (₺)"
              name="max_price"
              type="number"
              value={formData.max_price}
              onChange={handleChange}
              placeholder="Örn: 8000000"
            />
            <Input
              label="Min Oda Sayısı"
              name="min_room_count"
              type="number"
              value={formData.min_room_count}
              onChange={handleChange}
              placeholder="Örn: 2"
            />
          </div>
        </Card>

        <Card title="Bloklar" className="mb-6">
          <div className="space-y-3">
            {blocks.map((block, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  label={`Blok ${index + 1}`}
                  value={block.name}
                  onChange={(e) => handleBlockChange(index, e.target.value)}
                  placeholder="Örn: A Blok"
                  className="flex-1"
                />
                {blocks.length > 1 && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    onClick={() => removeBlock(index)}
                    className="mt-6"
                  >
                    <X size={16} />
                  </Button>
                )}
              </div>
            ))}
            <Button type="button" variant="secondary" size="sm" onClick={addBlock}>
              <Plus size={16} />
              Blok Ekle
            </Button>
          </div>
        </Card>

        <Card title="Ek Bilgiler" className="mb-6">
          <Textarea
            label="Açıklama"
            name="extra_info"
            value={formData.extra_info}
            onChange={handleChange}
            placeholder="Proje hakkında ek bilgiler..."
            rows={4}
          />
        </Card>

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/projects')}
          >
            İptal
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'Kaydediliyor...' : isEditMode ? 'Projeyi Güncelle' : 'Projeyi Kaydet'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProjectForm;
